/**
 * Market utilities — country-first URL routing layered above Polylang.
 *
 * URL structure:
 *   /              → default market (Sweden, sv, SEK)
 *   /en/...        → International English fallback (EU, en, EUR)
 *   /us/...        → US (en, USD) — same Polylang `en` content as /en/
 *   /ca/en/...     → Canada English (en, CAD)
 *   /ca/fr/...     → Canada French (fr, CAD)
 *
 * Polylang only knows about *languages* (sv, en, fr, ...). The country prefix
 * lives in this layer — `/us/`, `/uk/`, `/eu/` all fetch the same Polylang
 * `en` content. Currency / country / legal copy is handled here.
 *
 * Per-page overrides via slug convention: a page slugged `${prefix}-${slug}`
 * (e.g. `us-terms-and-conditions`) overrides the default for that market.
 * Resolution is two-step in App.jsx: try the prefixed slug first, then the
 * canonical one.
 */

// Hardcoded fallback. Used until loadMarkets() resolves, and as a safety net
// if the WP option is missing or malformed. Order doesn't matter — detectMarket
// sorts by prefix length so the longest match wins.
const MARKETS_FALLBACK = [
    { prefix: '',   country: 'SE', lang: 'sv', currency: 'SEK', label: 'Sverige' },
    { prefix: 'en', country: 'EU', lang: 'en', currency: 'EUR', label: 'International (English)' }
];

let markets = sortMarkets(MARKETS_FALLBACK);

function sortMarkets(arr) {
    return [...arr].sort((a, b) => b.prefix.length - a.prefix.length);
}

export const DEFAULT_MARKET = MARKETS_FALLBACK[0];

export function getMarkets() {
    return markets;
}

/**
 * Fetch the markets table from WordPress. Falls back silently on any error
 * so the site still works in dev / on a fresh install / if the option is empty.
 * Returns the active markets list.
 */
export async function loadMarkets() {
    try {
        const res = await fetch('/wp-json/cardcam/v1/markets');
        if (!res.ok) return markets;
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) return markets;
        // Validate minimal shape — any malformed entry → use fallback for safety.
        const valid = data.every(m =>
            m && typeof m.prefix === 'string'
              && typeof m.lang === 'string'
              && typeof m.country === 'string'
              && typeof m.currency === 'string'
        );
        if (!valid) {
            console.warn('cardcam_markets option malformed, using fallback');
            return markets;
        }
        markets = sortMarkets(data);
        return markets;
    } catch (e) {
        console.warn('Failed to load markets, using fallback:', e);
        return markets;
    }
}

/**
 * Match the longest-matching market prefix in the URL. Falls back to default.
 *   '/us/foo'      → prefix 'us'
 *   '/ca/en/foo'   → prefix 'ca/en' (must be sorted longest-first)
 *   '/foo'         → default (prefix '')
 */
export function detectMarket(pathname) {
    const path = (pathname || '/').replace(/^\//, '');
    for (const m of markets) {
        if (!m.prefix) continue;
        if (path === m.prefix || path.startsWith(m.prefix + '/')) {
            return m;
        }
    }
    return markets.find(m => m.prefix === '') || DEFAULT_MARKET;
}

/**
 * Strip the market prefix from a path and return the WP page slug.
 *   '/us/terms'    → 'terms'
 *   '/ca/en/terms' → 'terms'
 *   '/'            → 'home'
 */
export function getSlug(pathname) {
    const market = detectMarket(pathname);
    let path = (pathname || '/').replace(/^\//, '');
    if (market.prefix && (path === market.prefix || path.startsWith(market.prefix + '/'))) {
        path = path.slice(market.prefix.length);
    }
    return path.replace(/^\/+|\/+$/g, '') || 'home';
}

/**
 * Build the URL for a slug in a given market.
 *   buildMarketPath(seMarket, 'pricing')  → '/pricing'
 *   buildMarketPath(usMarket, 'pricing')  → '/us/pricing'
 *   buildMarketPath(caEnMarket, 'home')   → '/ca/en/'
 */
export function buildMarketPath(market, slug) {
    const m = market || DEFAULT_MARKET;
    const prefix = m.prefix ? `/${m.prefix}` : '';
    if (!slug || slug === 'home') {
        return prefix ? `${prefix}/` : '/';
    }
    return `${prefix}/${slug}`;
}

/**
 * Detect visitor's currency from navigator.language and persist it as the
 * cc_currency cookie. Runs synchronously at app load so the cookie is set
 * BEFORE any REST request for page content — that way the {price}
 * placeholder in PHP resolves to the correct currency on the first
 * page render. No reload needed.
 *
 * Lookup order:
 *   1. cc_currency cookie already set → respect it (user's switcher choice
 *      or previous auto-detection)
 *   2. Match navigator.language (e.g. "en-GB") against the markets table:
 *      first by lang + country, then by lang alone
 *   3. Hardcoded country → currency fallback for the common cases
 *
 * Uses MARKETS_FALLBACK as the source (synchronous) — the live markets
 * table is loaded async later by loadMarkets(), but for currency detection
 * the hardcoded list plus the fallback below is enough; users adding new
 * country/currency pairs in WP admin would need to extend the fallback
 * here too if they want pre-loadMarkets detection.
 */
export function detectAndSetCurrency() {
    if (typeof document === 'undefined') return; // SSR / non-browser safety
    if (document.cookie.split(';').some(c => c.trim().startsWith('cc_currency='))) return;

    const browserLang = (navigator.language || '').toLowerCase();
    if (!browserLang) return;

    const [lang, regionRaw] = browserLang.split('-');
    const country = (regionRaw || '').toUpperCase();

    const currency = matchCurrency(markets, lang, country)
                  ?? COUNTRY_CURRENCY_FALLBACK[country]
                  ?? null;
    if (!currency) return;

    document.cookie = `cc_currency=${currency};path=/;max-age=${365 * 24 * 3600};SameSite=Lax`;
    try { localStorage.setItem('cc_currency', currency); } catch { /* storage disabled — cookie still set */ }
}

function matchCurrency(marketsList, lang, country) {
    if (country) {
        for (const m of marketsList) {
            if (m.lang === lang && m.country === country) return m.currency;
        }
    }
    for (const m of marketsList) {
        if (m.lang === lang) return m.currency;
    }
    return null;
}

// Used when the markets table doesn't have a country match (e.g. UK visitor
// before /uk/ market is added, or US visitor with no /us/ market). Country
// codes are ISO 3166-1 alpha-2.
const COUNTRY_CURRENCY_FALLBACK = {
    GB: 'GBP',
    US: 'USD', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
    SE: 'SEK', NO: 'NOK', DK: 'DKK',
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
    BE: 'EUR', AT: 'EUR', PT: 'EUR', IE: 'EUR', FI: 'EUR',
    CH: 'CHF', JP: 'JPY',
};
